import { formatCurrency } from '../utilities/_commerce'

export const addToCart = ({title, url, preselectVariantId, shortDesc, showDiameter, variantsData}) => ({
	title,
	url,
	preselectVariantId,
	shortDesc,
	showDiameter,
	variants: variantsData,
	qty: 1,
	price: 0,
	originalPrice: 0,
	discount: 0,
	onSale: false,
	salePrice: 0,
	selectedColour: null,
	selectedStyle: null,
	selectedSize: null,
	selectedSizeType: null,
	selectedPowerPref: null,
	selectedVariant: null,

	init() {
		const currentUrl = new URL(window.location.href)

		if (this.preselectVariantId) {
			const preselectedVariant = this.variants.find(variant => variant.id === this.preselectVariantId)

			// If the preselected variant we try to find a variant and preselect it's values.
			// If the URL param doesn't match a variant, clear the param and reload to prevent JS errors.
			// This can happen if a user shares a link with an invalid or removed variant ID.
			//
			if (preselectedVariant) {
				this.selectedColour = this.normalizeColour(preselectedVariant.colour)
				this.selectedStyle = preselectedVariant.style || null
				this.selectedSize = preselectedVariant.size || null
				this.selectedSizeType = preselectedVariant.sizeType || null
				this.selectedPowerPref = preselectedVariant.powerPref || null
				this.updateSelectedVariant(true)
			} else {
				currentUrl.searchParams.delete('variant')
				window.history.replaceState({}, '', currentUrl)
				window.location.reload()
			}
		} else {
			this.initSelections()
		}

		// Set global dimensions/diameter
		//
		this.$store.product.selectedDimensions = this.dimensionsString ?? ''
		this.$store.product.selectedDiameter = this.diameterString ?? ''
		this.$store.product.shortDesc = this.shortDesc
		this.$store.product.showDiameter = this.showDiameter

		this.$watch('selectedVariant', () => {
			// Update URL with selected variant ID
			//
			if (this.selectedVariant?.id) {
				currentUrl.searchParams.set('variant', this.selectedVariant.id)
			} else {
				currentUrl.searchParams.delete('variant')
			}
			window.history.replaceState({}, '', currentUrl)

			// Set global dimensions/diameter
			//
			this.$store.product.selectedDimensions = this.dimensionsString ?? ''
			this.$store.product.selectedDiameter = this.diameterString ?? ''
		})
	},

	get hasDiscount() {
		return this.discount !== "0" && Number(this.discount) < 0
	},

	get discountedPrice() {
		if (this.hasDiscount) {
			return this.price + (Number(this.discount) / this.qty)
		}
		return this.onSale ? this.salePrice : this.price
	},

	get colours() {
		if (!this.variants) return []

    const seen = new Set()

    return this.variants
      .map(v => v.colour)
      .filter(colour => {
        if (seen.has(colour.id)) return false
        seen.add(colour.id)
        return true
      })
  },

	get styleOptions() {
		if (!this.variants) return []

		const seen = new Map()

		return this.variants
			.map(variant => {
				const styleType = typeof variant.styleType === 'string'
					? variant.styleType
					: (variant.styleType?.label || null)

				return {
					style: variant.style,
					styleType,
				}
			})
			.filter(option => {
				if (!option.style) return false
				const styleKey = option.style
				const typeKey = option.styleType || null

				if (!seen.has(styleKey)) {
					seen.set(styleKey, new Set())
				}

				const typeSet = seen.get(styleKey)
				if (typeSet.has(typeKey)) return false
				typeSet.add(typeKey)
				return true
			})
	},

	get sizeOptions() {
		if (!this.variants) return []

		const seen = new Map()

		return this.variants
			.map(variant => {
				const sizeType = typeof variant.sizeType === 'string'
					? variant.sizeType
					: (variant.sizeType?.label || null)

				return {
					size: variant.size,
					sizeType,
				}
			})
			.filter(option => {
				if (!option.size) return false
				const sizeKey = option.size
				const typeKey = option.sizeType || null

				if (!seen.has(sizeKey)) {
					seen.set(sizeKey, new Set())
				}

				const typeSet = seen.get(sizeKey)
				if (typeSet.has(typeKey)) return false
				typeSet.add(typeKey)
				return true
			})
			.sort((a, b) => {
				const aNumber = parseInt(String(a.size).match(/\d+/)?.[0] ?? '0', 10)
				const bNumber = parseInt(String(b.size).match(/\d+/)?.[0] ?? '0', 10)
				return aNumber - bNumber
			})
	},

	get powerPrefs() {
		if (!this.variants) return []

		const seen = new Set()

		return this.variants
			.map(v => v.powerPref)
			.filter(powerPref => {
				if (!powerPref) return false
				if (seen.has(powerPref)) return false
				seen.add(powerPref)
				return true
			})
	},

	initSelections() {
		const colours = this.colours !== null && this.colours.length ? this.colours : [null]
		const styleOptions = this.styleOptions.length ? this.styleOptions : [{ style: null, styleType: null }]
		const sizeOptions = this.sizeOptions.length ? this.sizeOptions : [{ size: null, sizeType: null }]
		const powerPrefs = this.powerPrefs.length ? this.powerPrefs : [null]

		for (const colour of colours) {
			for (const styleItem of styleOptions) {
				for (const sizeItem of sizeOptions) {
					for (const powerPref of powerPrefs) {
						const variant = this.findVariantFor(colour, styleItem?.style, sizeItem?.size, powerPref)
						if (!variant) continue

						this.selectedColour = this.normalizeColour(colour || variant.colour)
						this.selectedStyle = styleItem?.style || variant.style || null
						this.selectedSize = sizeItem?.size || variant.size || null
						this.selectedSizeType = sizeItem?.sizeType || variant.sizeType || null
						this.selectedPowerPref = powerPref || variant.powerPref || null

						this.updateSelectedVariant(true)

						if (this.selectedVariant) {
							return
						}
					}
				}
			}
		}
	},

	mapToAvailableVariant() {
		if (!this.variants || !this.variants.length) {
			this.selectedVariant = null
			return
		}

		let variant = this.findVariantFor(this.selectedColour, this.selectedStyle, this.selectedSize, this.selectedPowerPref)

		if (!variant) {
			variant = this.findVariantFor(this.selectedColour, this.selectedStyle, this.selectedSize, null)
		}

		if (!variant) {
			variant = this.findVariantFor(this.selectedColour, this.selectedStyle, null, null)
		}

		if (!variant) {
			variant = this.findVariantFor(this.selectedColour, null, null, null)
		}

		if (!variant) {
			variant = this.variants[0]
		}

		if (!variant) {
			this.selectedVariant = null
			return
		}

		this.selectedColour = this.normalizeColour(variant.colour)
		this.selectedStyle = variant.style || null
		this.selectedSize = variant.size || null
		this.selectedSizeType = variant.sizeType || null
		this.selectedPowerPref = variant.powerPref || null
	},

	selectColour(colour) {
		this.selectedColour = this.normalizeColour(colour)

		this.mapToAvailableVariant()
		this.updateSelectedVariant()
	},

	selectStyle(style) {
		this.selectedStyle = style

		this.mapToAvailableVariant()
		this.updateSelectedVariant()
	},

	selectSize(size, sizeType) {
		this.selectedSize = size
		this.selectedSizeType = sizeType

		this.mapToAvailableVariant()
		this.updateSelectedVariant()
	},

	selectPowerPref(powerPref) {
		this.selectedPowerPref = powerPref
		this.mapToAvailableVariant()
		this.updateSelectedVariant()
	},

	styleIsAvailable(style) {
		return !!this.findVariantFor(this.selectedColour, style)
	},

	sizeIsAvailable(size) {
		return !!this.findVariantFor(this.selectedColour, this.selectedStyle, size)
	},

	powerPrefIsAvailable(powerPref) {
		return !!this.findVariantFor(this.selectedColour, this.selectedStyle, this.selectedSize, powerPref)
	},

	findVariantFor(colour, style, size, powerPref) {
		if (!this.variants) return null
		const colourTitle = this.normalizeColour(colour)

		return this.variants.find(variant => {
			if (colourTitle && variant.colour?.title !== colourTitle) return false
			if (style && variant.style !== style) return false
			if (size && variant.size !== size) return false
			if (powerPref && variant.powerPref !== powerPref) return false
			return true
		}) || null
	},

	findVariant() {
		if (!this.variants) return null
		const colourTitle = this.normalizeColour(this.selectedColour)

		return this.variants.find(variant => {
			if (this.selectedStyle && variant.style !== this.selectedStyle) return false
			if (this.selectedSize && variant.size !== this.selectedSize) return false
			if (colourTitle && variant.colour?.title !== colourTitle) return false
			if (this.selectedPowerPref && variant.powerPref !== this.selectedPowerPref) return false
			return true
		}) || null
	},

	normalizeColour(colour) {
		if (!colour) return null
		if (typeof colour === 'string') {
			return colour
		}
		return colour.title || null
	},

	updateSelectedVariant(init = false) {
		this.selectedVariant = this.findVariant()

		if (!this.selectedVariant) return

		this.price = this.selectedVariant.price
		this.onSale = this.selectedVariant.isOnPromotion
		this.salePrice = this.selectedVariant.salePrice

		// Emit event for sliderProduct to track changes, delay on initial load
		// to ensure slider is ready to receive event
		//
		if (init) {
			setTimeout(() => {
				window.dispatchEvent(new CustomEvent('variantUpdated', {
					detail: {
						id: this.selectedVariant.id,
					}
				}))
			}, 1000)
		} else {
			window.dispatchEvent(new CustomEvent('variantUpdated', {
				detail: {
					id: this.selectedVariant.id,
				}
			}))
		}
	},

	qtyDown() {
		if (this.qty > 1) {
			this.qty--
		}
	},

	qtyUp() {
		this.qty++
	},

	getPrice(singular = false) {
		if (this.onSale) {
			if (singular) {
				return formatCurrency(this.salePrice)
			} else {
				return formatCurrency(this.salePrice * this.qty)
			}
		} else {
			if (singular) {
				return formatCurrency(this.price)
			} else {
				return formatCurrency(this.price * this.qty)
			}
		}
	},

	getOriginalPrice() {
		return formatCurrency(this.price * this.qty)
	},

	get variantOptionsString() {
		const options = []

		if (this.selectedColour) {
			options.push(this.selectedColour)
		}

		if (this.selectedStyle) {
			options.push(this.selectedStyle)
		}

		if (this.selectedSize) {
			const sizeOption = this.sizeOptions.find(option => option.size === this.selectedSize)
			if (sizeOption?.sizeType) {
				options.push(sizeOption.sizeType + ' ' + sizeOption.size + ' cm')
			} else {
				options.push(this.selectedSize + ' cm')
			}
		}

		if (this.selectedPowerPref !== 'N/A') {
			options.push(this.selectedPowerPref)
		}

		return options.join(', ')
	},

	get dimensionsString() {
		const dimensions = []

		if (this.shortDesc) {
			return this.shortDesc
		}

		if (this.selectedVariant?.dimensions?.length) {
			dimensions.push('L' + this.selectedVariant.dimensions.length)
		}

		if (this.selectedVariant?.dimensions?.width) {
			dimensions.push('W' + this.selectedVariant.dimensions.width)
		}

		if (this.selectedVariant?.dimensions?.height) {
			dimensions.push('H' + this.selectedVariant.dimensions.height)
		}

		dimensions.push(' cm')

		return dimensions.join(' x ')
	},

	get diameterString() {
		if (this.shortDesc) {
			return this.shortDesc
		}

		if (this.selectedVariant?.dimensions?.length) {
			return this.selectedVariant.dimensions.length + ' cm diameter'
		}

		return ''
	},

	async add() {
		if (!this.selectedVariant) return

		this.$store.cart.add(
			this.selectedVariant.id,
			this.title,
			this.onSale ? this.selectedVariant.salePrice : this.selectedVariant.price,
			this.qty,
			this.url + (this.selectedVariant ? `?variant=${this.selectedVariant.id}` : ''),
			this.variantOptionsString
		)

		setTimeout(() => {
			this.$store.cart.getCart()
		}, 500)
	},
})
